import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { and, not } from 'tio-ui/utilities';
import { flatten, safeParse } from 'valibot';
import { PhArrowLeft, PhArrowRight } from 'ember-phosphor-icons';
import { initializeTypedProgramRequest } from 'tio-data/requests/tas-program-template';
import {
  dynamicEligibilityRequest,
  periodsOfEligibilityRequest,
} from 'tio-data/requests/tas-participant-actions';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Heading from 'tio-ui/components/heading';
import Modal from 'tio-ui/components/modal';
import Text from 'tio-ui/components/text';
import AlertBanner from 'tio-ui/components/alert-banner';
import Flatpickr from 'tio-employee/components/flatpickr';
import { formatDateToISO } from 'tio-common/utils/date';
import {
  getSingleValueForTasField,
  getFieldLabelForFieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import { hasInProgressInstanceOfCurrentProgramTemplate } from 'tio-common/utils/tuition-assistance/tas-grouping';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import type RouterService from '@ember/routing/router';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import type { TasGroup as TasGroupModel } from 'tio-data/data/resources/tas-group.type';
import type { TasParticipantExtension as TasParticipantModel } from 'tio-data/data/resources/tas-participant.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type FeaturesService from 'tio-common/services/features';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type { EligibleTemplate, LiveTasProgram } from 'tio-employee/services/reimagined-data';
import type { DynamicEligibility, PeriodOfEligibility } from 'tio-common/types/tuition-assistance';

type ModalView = 'list' | 'loading' | 'date-check' | 'error';

export interface AddApplicationModalSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    enrolledPrograms: LiveTasProgram[];
    eligibleTemplates: EligibleTemplate[];
  };
}

export default class AddApplicationModal extends Component<AddApplicationModalSignature> {
  @service declare features: FeaturesService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  @tracked view: ModalView = 'list';
  @tracked selectedTemplate: EligibleTemplate | null = null;
  @tracked templateModel: TasProgramTemplateModel | null = null;
  @tracked participantModel: TasParticipantModel | null = null;
  @tracked tasGroups: TasGroupModel[] = [];
  @tracked activeInstances: TasProgramInstanceModel[] = [];
  @tracked guardError = '';
  @tracked isSubmitting = false;
  @tracked dateForDateCheck = '';
  @tracked dynamicEligibility?: boolean;
  @tracked programEligibility: [string, string][] = [];
  @tracked eligibilityError = false;

  // --- Enrolled programs (Type 4) ---

  selectProgram = (programId: string) => {
    this.args.onClose();
    this.resetState();
    this.router.transitionTo('authenticated.tas.programs.show', programId, {
      queryParams: { fromModal: 'true' },
    });
  };

  // --- Eligible templates ---

  get sortedEligibleTemplates() {
    return [...this.args.eligibleTemplates].sort((a, b) => a.name.localeCompare(b.name));
  }

  selectTemplate = (template: EligibleTemplate) => {
    const isType2or3 =
      template.typeClassification === 'TAS.ProgramType.2' ||
      template.typeClassification === 'TAS.ProgramType.3';

    if (isType2or3) {
      this.handleType2or3(template);
    } else {
      // Type 1/4 → route to getting-started
      this.args.onClose();
      this.resetState();
      this.router.transitionTo('authenticated.tas.getting-started', template.id);
    }
  };

  handleType2or3 = async (template: EligibleTemplate) => {
    this.selectedTemplate = template;
    this.view = 'loading';

    try {
      await this.loadGuardData(template.id);
      const error = this.evaluateGuards();

      if (error) {
        this.guardError = error;
        this.view = 'error';
        return;
      }

      // Guards passed — check if date eligibility is needed
      if (this.requiresDateCheck) {
        // Preload dynamic eligibility now so date selection reacts immediately
        if (this.participantModel && this.templateModel) {
          const id = this.participantModel.id as string;
          const { code } = this.templateModel;
          const [dynamicResponse, periodsResponse] = await Promise.all([
            this.store.request(dynamicEligibilityRequest(id)),
            this.store.request(periodsOfEligibilityRequest(id)),
          ]);
          // Legacy adapter filtered the per-program-code value off the raw response.
          const dynamicEligibility = dynamicResponse.content as DynamicEligibility;
          const programEligibility = periodsResponse.content as PeriodOfEligibility;
          this.dynamicEligibility = dynamicEligibility[code];
          this.programEligibility = programEligibility[code] as [string, string][];
        }
        this.view = 'date-check';
      } else {
        // No date needed — apply directly
        await this.applyToProgram();
      }
    } catch {
      this.guardError = 'Unable to verify eligibility. Please try again.';
      this.view = 'error';
    }
  };

  // --- Data loading ---

  loadGuardData = async (templateId: string) => {
    const employeeId = this.sessionContext.currentEmployee.id;
    const companyId = this.sessionContext.currentEmployee.company.id;

    const [
      { content: templateContent },
      { content: participantsContent },
      { content: tasGroupsContent },
      { content: activeInstancesContent },
    ] = (await Promise.all([
      this.store.request(
        findRecord<TasProgramTemplateModel>('tas-program-template', templateId, {
          include: 'conditions.public-assets',
          reload: true,
        })
      ),
      this.store.request(
        query<TasParticipantModel>('tas-participant', {
          'filter[employee]': employeeId,
          include: 'employee.dependents,tas-eligibilities',
        })
      ),
      this.store.request(
        query<TasGroupModel>('tas-group', {
          'filter[company]': companyId,
          'filter[flavor]': 'TAS.GroupFlavor.MUTUALLY_EXCLUSIVE',
          include: 'tas-groupings.tas-program-template',
        })
      ),
      this.store.request(
        query<TasProgramInstanceModel>('tas-program-instance', {
          'filter[employee]': employeeId,
          'filter[active]': true,
          include: 'tas-program-template',
        })
      ),
    ])) as [
      { content: { data: TasProgramTemplateModel } },
      { content: { data: TasParticipantModel[] } },
      { content: { data: TasGroupModel[] } },
      { content: { data: TasProgramInstanceModel[] } },
    ];

    this.templateModel = templateContent.data;
    this.participantModel = participantsContent.data[0] ?? null;
    this.tasGroups = [...tasGroupsContent.data];
    this.activeInstances = [...activeInstancesContent.data];

    if (this.participantModel) {
      await this.tuitionAssistance.loadEligibilitiesForCurrentEmployee(this.participantModel);
    }
  };

  // --- Guard evaluation ---

  evaluateGuards = (): string | null => {
    if (!this.templateModel || !this.participantModel) {
      return 'Unable to load program data.';
    }

    if (this.templateModel.state !== 'TAS.ProgramTemplateState.LAUNCHED') {
      return 'This program is not currently accepting applications.';
    }

    if (this.participantModel.isHeld) {
      return 'Your tuition assistance account is currently on hold. Please contact your administrator.';
    }

    if (!this.tuitionAssistance.hasActiveEligibilityForProgramTemplate(this.templateModel)) {
      return 'You do not have active eligibility for this program.';
    }

    if (
      this.tasGroups.length > 0 &&
      this.templateModel.id &&
      hasInProgressInstanceOfCurrentProgramTemplate(
        this.tasGroups,
        this.templateModel.id,
        this.activeInstances
      )
    ) {
      return 'You already have an in-progress application for a mutually exclusive program.';
    }

    if (
      this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
        this.templateModel.eligibilityWaitingPeriod,
        this.participantModel
      )
    ) {
      return 'You have not yet met the waiting period requirement for this program.';
    }

    // Check for existing active instance (non-multiple-instance programs)
    if (!this.templateModel.isMultipleInstanceProgram && this.activeInstances.length > 0) {
      const hasActive = this.activeInstances.some(
        (instance) => instance.tasProgramTemplate.id === this.templateModel!.id
      );
      if (hasActive) {
        return 'You already have an active application for this program.';
      }
    }

    return null;
  };

  // --- Date check ---

  setDateForDateCheck = (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      this.dateForDateCheck = formatDateToISO(selectedDates[0]);
      this.eligibilityError = false;

      // Re-check eligibility if data already loaded
      if (this.dynamicEligibility !== undefined && this.programEligibility.length) {
        this.eligibilityError = !this.calculateEligibility();
      }
    }
  };

  get lockStatusLabel() {
    if (!this.templateModel) return '';
    const lock = this.templateModel.lockEmployeeStatusBasedOn;
    const startLabel =
      (getFieldLabelForFieldName('COURSES_BEGIN_DATE', this.templateModel.fields) as string) ||
      'Start Date';
    const endLabel =
      (getFieldLabelForFieldName('COURSES_END_DATE', this.templateModel.fields) as string) ||
      'End Date';
    const labelMap: Record<string, string> = {
      COURSE_START_DATE: startLabel.toLowerCase(),
      COURSE_END_DATE: endLabel.toLowerCase(),
    };
    return labelMap[lock] ?? '';
  }

  get lockStatus() {
    if (!this.templateModel) return '';
    const lockType: Record<string, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    return this.templateModel.lockEmployeeStatusBasedOn in lockType
      ? lockType[this.templateModel.lockEmployeeStatusBasedOn]
      : '';
  }

  calculateEligibility = () => {
    if (!this.programEligibility.length) return false;
    const enteredDate = new Date(this.dateForDateCheck);
    return this.programEligibility.some(([start, end]) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      return enteredDate >= startDate && enteredDate <= endDate;
    });
  };

  get checkDateEligibility() {
    return (
      !this.lockStatus ||
      this.lockStatus === 'fulfilled' ||
      (this.dynamicEligibility && this.calculateEligibility())
    );
  }

  get formValidationErrors() {
    if (
      !this.templateModel ||
      this.templateModel.lockEmployeeStatusBasedOn !== 'COURSE_START_DATE'
    ) {
      return {};
    }
    if (!this.dateForDateCheck) return {};

    const schema = this.tuitionAssistance.dynamicBeginDateValidationSchema(this.templateModel);
    const result = safeParse(
      schema,
      { dateEntered: new Date(this.dateForDateCheck) },
      { abortEarly: false }
    );
    if (!result.success) {
      const flattened = flatten(result.issues);
      return flattened.nested ?? {};
    }
    return {};
  }

  get hasValidationErrors() {
    return Object.keys(this.formValidationErrors).length > 0;
  }

  get validationErrorMessages() {
    const errors = this.formValidationErrors;
    const messages: string[] = [];
    for (const key of Object.keys(errors)) {
      const fieldErrors = errors[key];
      if (fieldErrors) {
        messages.push(...fieldErrors);
      }
    }
    return messages;
  }

  get eligibleBasedOnWaitingPeriod() {
    if (!this.templateModel || !this.participantModel) return true;
    const dateToCheck =
      this.templateModel.lockEmployeeStatusBasedOn === 'COURSE_START_DATE'
        ? this.dateForDateCheck
        : undefined;
    return !this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
      this.templateModel.eligibilityWaitingPeriod,
      this.participantModel,
      dateToCheck
    );
  }

  get canApplyWithDate() {
    return !!this.dateForDateCheck && !this.eligibilityError && !this.hasValidationErrors;
  }

  applyWithDate = async () => {
    if (!this.dateForDateCheck || !this.templateModel || !this.participantModel) return;
    if (this.hasValidationErrors) return;

    this.isSubmitting = true;

    try {
      // Check date eligibility against periods of eligibility
      if (!this.checkDateEligibility) {
        this.eligibilityError = true;
        this.isSubmitting = false;
        return;
      }

      // All checks passed — apply
      await this.applyToProgram(this.dateForDateCheck);
    } catch {
      this.guardError = 'Unable to verify eligibility. Please try again.';
      this.view = 'error';
    } finally {
      this.isSubmitting = false;
    }
  };

  get requiresDateCheck() {
    if (!this.templateModel) return false;
    const lock = getSingleValueForTasField(
      'LOCK_EMPLOYEE_STATUS_BASED_ON',
      this.templateModel.fields
    );
    return lock === 'COURSE_START_DATE' || lock === 'COURSE_END_DATE';
  }

  get canApplyToProgram() {
    return this.evaluateGuards() === null;
  }

  // --- Apply ---

  applyToProgram = async (date?: string) => {
    if (!this.templateModel) return;
    this.isSubmitting = true;

    try {
      const employeeId = this.sessionContext.currentEmployee.id as string;
      const response = await this.store.request(
        initializeTypedProgramRequest({
          templateId: this.templateModel.id as string,
          employeeId,
          eligibilityDate: date || new Date().toLocaleDateString('en-CA'),
        })
      );
      const newProgram = (response.content as { data: { id: string; type: string } }).data;

      const typeMap: Record<string, string> = {
        'TAS.ProgramType.2': 'type-two',
        'TAS.ProgramType.3': 'type-three',
        'TAS.ProgramType.4.2': 'type-four',
        'TAS.ProgramType.4.3': 'type-four',
      };
      const typeSlug = typeMap[this.templateModel.typeClassification] ?? 'type-four';
      // Guided wizard is the only application flow.
      const route = `authenticated.tas.applications.${typeSlug}-guided.new`;

      this.args.onClose();
      this.resetState();
      this.router.transitionTo(route, newProgram.id, {
        queryParams: date ? { date } : {},
      });
    } catch {
      this.guardError = 'Failed to create application. Please try again.';
      this.view = 'error';
    } finally {
      this.isSubmitting = false;
    }
  };

  // --- Navigation ---

  goBack = () => {
    this.view = 'list';
    this.selectedTemplate = null;
    this.guardError = '';
  };

  handleClose = () => {
    this.args.onClose();
    this.resetState();
  };

  resetState = () => {
    this.view = 'list';
    this.selectedTemplate = null;
    this.templateModel = null;
    this.participantModel = null;
    this.tasGroups = [];
    this.activeInstances = [];
    this.guardError = '';
    this.isSubmitting = false;
    this.dateForDateCheck = '';
    this.dynamicEligibility = undefined;
    this.programEligibility = [];
    this.eligibilityError = false;
  };

  isType4 = (typeClassification: string) => {
    return typeClassification.startsWith('TAS.ProgramType.4');
  };

  get isViewList() {
    return this.view === 'list';
  }
  get isViewLoading() {
    return this.view === 'loading';
  }
  get isViewError() {
    return this.view === 'error';
  }
  get isViewDateCheck() {
    return this.view === 'date-check';
  }

  <template>
    <Modal @isOpen={{@isOpen}} @onClose={{this.handleClose}} @size="md">
      <:header>
        {{#if this.selectedTemplate}}
          <div class="tio-row-tight">
            <button
              type="button"
              class="rounded-md p-1 tio-fg-disabled hover:tio-fg-secondary"
              {{on "click" this.goBack}}
            >
              <PhArrowLeft class="tio-icon-sm" />
            </button>
            <Heading @level={{2}} @size="lg">
              {{this.selectedTemplate.name}}
            </Heading>
          </div>
        {{else}}
          <Heading @level={{2}} @size="lg">
            {{t "tas.add_application_modal.title"}}
          </Heading>
        {{/if}}
      </:header>
      <:body>
        {{#if this.isViewList}}
          <div class="tio-col-loose">
            {{#if @enrolledPrograms.length}}
              <div class="tio-col-tight">
                <div>
                  <Heading @level={{3}} @size="sm">
                    {{t "tas.add_application_modal.enrolled_section"}}
                  </Heading>
                  <Text @size="sm" class="tio-fg-muted">
                    {{t "tas.add_application_modal.enrolled_hint"}}
                  </Text>
                </div>
                <div class="tio-col-flush gap-1.5">
                  {{#each @enrolledPrograms as |program|}}
                    <button
                      type="button"
                      class="group tio-row-between rounded-lg border tio-border-default tio-bg-surface px-3 py-2.5 text-left transition-all hover:tio-border-brand hover:shadow-sm"
                      {{on "click" (fn this.selectProgram program.id)}}
                    >
                      <div class="tio-col-flush gap-0.5">
                        <span class="tio-text-sm-medium tio-fg-primary group-hover:tio-fg-brand">
                          {{program.name}}
                        </span>
                        {{#if program.institution}}
                          <span class="text-xs tio-fg-muted">{{program.institution}}</span>
                        {{/if}}
                      </div>
                      <PhArrowRight
                        class="tio-icon-sm shrink-0 tio-fg-disabled group-hover:tio-fg-brand"
                      />
                    </button>
                  {{/each}}
                </div>
              </div>
            {{/if}}

            <div class="tio-col-tight">
              <div>
                <Heading @level={{3}} @size="sm">
                  {{t "tas.add_application_modal.eligible_section"}}
                </Heading>
                <Text @size="sm" class="tio-fg-muted">
                  {{t "tas.add_application_modal.eligible_hint"}}
                </Text>
              </div>
              {{#if @eligibleTemplates.length}}
                <div class="tio-col-flush gap-1.5">
                  {{#each this.sortedEligibleTemplates as |template|}}
                    <button
                      type="button"
                      class="group tio-row-between rounded-lg border tio-border-default tio-bg-surface px-3 py-2.5 text-left transition-all hover:tio-border-brand hover:shadow-sm"
                      {{on "click" (fn this.selectTemplate template)}}
                    >
                      <div class="tio-col-flush gap-0.5">
                        <div class="tio-row-tight">
                          <span class="tio-text-sm-medium tio-fg-primary group-hover:tio-fg-brand">
                            {{template.name}}
                          </span>
                          {{#if template.isEnrolled}}
                            {{#if (this.isType4 template.typeClassification)}}
                              <Chip @appearance="faded" @intent="success" @size="sm">
                                {{t "tas.dashboard.chips.enrolled"}}
                              </Chip>
                            {{/if}}
                          {{/if}}
                          {{#if template.isTrial}}
                            <Chip @appearance="faded" @intent="warning" @size="sm">
                              {{t "tas.dashboard.chips.trial"}}
                            </Chip>
                          {{/if}}
                        </div>
                        {{#if template.description}}
                          <span class="text-xs tio-fg-muted line-clamp-1">
                            {{template.description}}
                          </span>
                        {{/if}}
                      </div>
                      <PhArrowRight
                        class="tio-icon-sm shrink-0 tio-fg-disabled group-hover:tio-fg-brand"
                      />
                    </button>
                  {{/each}}
                </div>
              {{else}}
                <Text @size="sm" class="tio-fg-disabled">
                  {{t "tas.add_application_modal.no_programs"}}
                </Text>
              {{/if}}
            </div>
          </div>

        {{else if this.isViewLoading}}
          <div class="tio-place-center py-12 gap-3">
            <div
              class="h-8 w-8 animate-spin rounded-full border-4 tio-border-default border-t-violet-500"
            ></div>
            <Text @size="sm" class="tio-fg-muted">
              {{t "tas.add_application_modal.checking_eligibility"}}
            </Text>
          </div>

        {{else if this.isViewError}}
          <div class="tio-col-loose">
            <AlertBanner @intent="warning">
              <p class="text-sm">{{this.guardError}}</p>
            </AlertBanner>
            <Button @appearance="outlined" {{on "click" this.goBack}}>
              {{t "tas.add_application_modal.back_to_list"}}
            </Button>
          </div>

        {{else if this.isViewDateCheck}}
          <div class="tio-col-loose">
            <Text @size="sm" class="tio-fg-secondary">
              {{t "start_date_eligibility.instruction" startEndSubmission=this.lockStatusLabel}}
            </Text>
            <div class="tio-flex-center">
              <Flatpickr
                @date={{this.dateForDateCheck}}
                @onChange={{this.setDateForDateCheck}}
                @dateFormat="Y-m-d"
                @inline={{true}}
                @disableMobile={{true}}
                class="hidden"
              />
            </div>
            {{#if this.eligibilityError}}
              <AlertBanner
                @intent="warning"
                @title={{t
                  "start_date_eligibility.errorHeader"
                  startEndSubmission=this.lockStatusLabel
                }}
              >
                <p class="text-sm">
                  {{t "start_date_eligibility.errorText"}}
                </p>
              </AlertBanner>
            {{/if}}
            {{#if this.hasValidationErrors}}
              <AlertBanner @intent="warning">
                {{#each this.validationErrorMessages as |error|}}
                  <p class="text-sm">{{error}}</p>
                {{/each}}
              </AlertBanner>
            {{/if}}
            {{#if (and this.dateForDateCheck (not this.eligibleBasedOnWaitingPeriod))}}
              <AlertBanner
                @intent="warning"
                @title={{t
                  "tuition_assistance.application_start_date_eligibility_warnings.dashboard_title"
                }}
              >
                <p class="text-sm">
                  {{t
                    "tuition_assistance.application_start_date_eligibility_warnings.dashboard_description"
                    days=this.templateModel.eligibilityWaitingPeriod
                    htmlSafe=true
                  }}
                </p>
              </AlertBanner>
            {{/if}}
            <Button
              @intent="primary"
              @isDisabled={{not this.canApplyWithDate}}
              @isRunning={{this.isSubmitting}}
              {{on "click" this.applyWithDate}}
              class="w-full"
            >
              {{t "tas.add_application_modal.start_application"}}
            </Button>
          </div>
        {{/if}}
      </:body>
    </Modal>
  </template>
}
